import { definedMap } from "@glideapps/ts-necessities";

import { SerializedComponent } from "../../components/SerializedApp";
import {
    ArrayScreenFormat,
    ArrayItemType,
    ArrayScreenDescription,
    ScreenDescriptionKind,
    TitleDescription,
    ImageKind,
    ArrayContentDescription,
    LegacyPropertyDescription,
    TableGlideType,
    Description,
    MutatingScreenKind,
    ActionKind,
    InputOutputTables,
    arrayScreenName,
    makeTableRef,
} from "../description";
import { PropertyDescriptor, ComponentBuilder, PropertyTableGetter, ComponentConfiguratorContext } from "../components";
import { summaryForTableRef } from "../builder-utils";
import { getSummarySearchProperties, summaryColumnsForTable } from "../description-utils";
import { makeSummaryPropertyDescriptors, useFallbackInitialsPropertyDescriptor } from "../components/descriptor-utils";
import { getSwitchProperty } from "../components/property-utils";
import { ArrayScreenHandlerBase } from "./array-screen";
import { IconName } from "../../named-icons";
import { InlineListBuild } from "./array-content";
import { SwitchPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { pushHorizontalAndTruncatePropertyHandlers, truncateListPropertyHandler } from "./properties";
import { SerializedElementBuilder } from "../serialized-component";
import { ListItemFlags } from "./list-item-flags";
import { handlerForActionKind } from "../actions";
import { makeDropImageActionForColumnDescription } from "../components/title";

export interface SummaryArrayContentDescription extends TitleDescription, ArrayContentDescription {}

export interface SummaryArrayScreenDescription extends ArrayScreenDescription, SummaryArrayContentDescription {
    readonly kind: ScreenDescriptionKind.Array;
    readonly type: ArrayItemType;

    readonly format: ArrayScreenFormat;
}

export function descriptionIsSummaryArrayContent(desc: Description): desc is SummaryArrayContentDescription {
    const d = desc as SummaryArrayContentDescription;
    return (
        d.reverse !== undefined ||
        d.titleProperty !== undefined ||
        d.subtitleProperty !== undefined ||
        d.imageURLProperty !== undefined
    );
}

export function summaryAndPropertyDescriptionForArray(
    table: TableGlideType,
    makeSummaryIfPossible: boolean
): TitleDescription | undefined {
    return summaryColumnsForTable(table, false, false, makeSummaryIfPossible, true);
}

export function getNumberToTruncateTo(
    desc: SummaryArrayContentDescription,
    insideInlineList: boolean,
    roundTo: number,
    supportGrouping: boolean
): number | undefined {
    let numToTruncate: number | undefined;
    if (insideInlineList || supportGrouping) {
        numToTruncate = truncateListPropertyHandler.getNumber(desc);
        if (numToTruncate === undefined && legacyTruncateListPropertyHandler.getSwitch(desc)) {
            numToTruncate = Math.round(Math.ceil(4 / roundTo) * roundTo);
        }
    }
    return numToTruncate;
}

export const legacyTruncateListPropertyHandler = new SwitchPropertyHandler(
    { truncate: false },
    "Only show a few items",
    PropertySection.Options
);

export abstract class SummaryArrayScreenHandler<
    TContentDesc extends SummaryArrayContentDescription,
    TScreenDesc extends SummaryArrayScreenDescription & TContentDesc
> extends ArrayScreenHandlerBase<TContentDesc, TScreenDesc> {
    protected abstract readonly supportsNonURLImages: boolean;
    protected abstract readonly supportsEmojiImages: boolean;
    protected abstract readonly supportsTruncateList: boolean;

    constructor(
        format: ArrayScreenFormat,
        label: string,
        icon: IconName,
        private readonly _imageRequired: boolean | undefined
    ) {
        super(format, label, icon);
    }

    protected get imagePropertyFirst(): boolean {
        return false;
    }

    protected makeDetailScreenTitle(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        ctx: SerializedComponent
    ): SerializedComponent | undefined {
        return summaryForTableRef(builder, table, desc, ctx)[0];
    }

    public makeContentForProcessedArray(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        arrayData: SerializedComponent,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        uniqueID: string,
        caption: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;
        const [title, subtitle, image] = summaryForTableRef(builder, table, desc, b.getContext);
        const onTap = this.makeDefaultAction(builder, desc, table);
        let onDropImage: SerializedComponent | undefined;
        if (image !== undefined) {
            onDropImage = makeDropImageActionForColumnDescription(b, table, desc.imageURLProperty);
        }

        return this.content(
            builder,
            desc,
            table,
            arrayData,
            title,
            subtitle,
            image,
            onTap,
            onDropImage,
            searchActiveFlag,
            inlineListBuild,
            uniqueID,
            caption,
            this.getItemID(b, b.getContext, table)
        );
    }

    public getBasicSearchProperties(desc: TContentDesc): readonly string[] {
        return getSummarySearchProperties(desc);
    }

    protected abstract content(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent | undefined,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        uniqueID: string,
        caption: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent;

    protected includeDescription(): boolean {
        return true;
    }

    protected titleRequired(): boolean {
        return true;
    }

    protected getInterSummaryPropertyDescriptors<T extends TContentDesc>(
        _getPropertyTable: PropertyTableGetter<T> | undefined,
        _insideInlineList: boolean,
        _desc: T | undefined
    ): readonly PropertyDescriptor[] {
        return [];
    }

    public getContentPropertyDescriptors<T extends TContentDesc>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean
    ): readonly PropertyDescriptor[] {
        const result = [
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms,
                undefined
            ),
            ...makeSummaryPropertyDescriptors(
                this.titleRequired(),
                this.includeDescription(),
                this._imageRequired,
                this.supportsNonURLImages ? undefined : ImageKind.URL,
                getPropertyTable,
                this.supportsEmojiImages,
                this.imagePropertyFirst,
                this.getInterSummaryPropertyDescriptors(getPropertyTable, insideInlineList, desc)
            ),
        ];

        if (this.supportsTruncateList) {
            pushHorizontalAndTruncatePropertyHandlers(result, desc, isDefaultArrayScreen, false);
        }

        return result;
    }

    public getScreensUsed(
        desc: TContentDesc,
        tables: InputOutputTables | undefined,
        insideInlineList: boolean
    ): readonly string[] {
        if (
            tables !== undefined &&
            getNumberToTruncateTo(desc, insideInlineList, 1, this.supportsGrouping) !== undefined
        ) {
            return [arrayScreenName(makeTableRef(tables.input))];
        }

        return [];
    }
}

export interface FallbackInitialsSummaryArrayContentDescription extends SummaryArrayContentDescription {
    readonly useFallbackInitials?: LegacyPropertyDescription;
}
export interface FallbackInitialsSummaryArrayScreenDescription
    extends SummaryArrayScreenDescription,
        FallbackInitialsSummaryArrayContentDescription {}

export abstract class FallbackInitialsSummaryArrayScreenHandler<
    TContentDesc extends FallbackInitialsSummaryArrayContentDescription,
    TScreenDesc extends FallbackInitialsSummaryArrayScreenDescription & TContentDesc
> extends SummaryArrayScreenHandler<TContentDesc, TScreenDesc> {
    protected readonly fullTitleFallback: boolean = false;

    protected abstract contentWithFallbackInitials(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent | undefined,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        initials: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        caption: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent;

    protected content(
        builder: ComponentBuilder,
        desc: TContentDesc,
        table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent | undefined,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        _uniqueID: string,
        caption: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;
        let initials: SerializedComponent | undefined;
        if (getSwitchProperty(desc.useFallbackInitials) !== false && title !== undefined) {
            if (this.fullTitleFallback) {
                initials = b.withDefault(title, b.constant(""));
            } else {
                initials = b.extractInitials(title);
            }
        }
        return this.contentWithFallbackInitials(
            builder,
            desc,
            table,
            arrayData,
            title,
            subtitle,
            image,
            initials,
            onTap,
            onDropImage,
            searchActiveFlag,
            inlineListBuild,
            caption,
            itemID
        );
    }

    protected getAdditionalPropertyDescriptors<T extends TContentDesc>(
        _getPropertyTable: PropertyTableGetter<T> | undefined,
        _insideInlineList: boolean,
        _desc: T,
        _isDefaultArrayScreen: boolean
    ): readonly PropertyDescriptor[] {
        return [];
    }

    protected getEnableFallbackInitials(_desc: TContentDesc): boolean {
        return true;
    }

    public getContentPropertyDescriptors<T extends TContentDesc>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean
    ): ReadonlyArray<PropertyDescriptor> {
        return [
            ...super.getContentPropertyDescriptors(
                getPropertyTable,
                insideInlineList,
                desc,
                ccc,
                mutatingScreenKind,
                isDefaultArrayScreen,
                withTransforms
            ),
            ...this.getAdditionalPropertyDescriptors(getPropertyTable, insideInlineList, desc, isDefaultArrayScreen),
            ...(this.getEnableFallbackInitials(desc) ? [useFallbackInitialsPropertyDescriptor] : []),
        ];
    }

    public makeIconAndFlagsForDefaultAction(
        b: SerializedElementBuilder,
        desc: TContentDesc,
        flags: ListItemFlags
    ): { icon: SerializedComponent | undefined; flags: ListItemFlags } {
        let icon: SerializedComponent | undefined;
        const action = this.getActions(desc, undefined).actions[0];
        if (action !== undefined) {
            const iconName = definedMap(handlerForActionKind(action.kind), h => h.iconName);
            if (iconName === undefined) {
                flags |= ListItemFlags.DisableChevron;
            }
            if (action.kind !== ActionKind.PushDetailScreen && typeof iconName === "string") {
                icon = b.constant(iconName);
            }
        }
        return { icon, flags };
    }
}

export function isolateFallbackInitialsSummaryArrayContentDescription(
    desc: FallbackInitialsSummaryArrayContentDescription
): FallbackInitialsSummaryArrayContentDescription {
    return {
        useFallbackInitials: desc.useFallbackInitials,
        reverse: desc.reverse,
        groupByColumn: desc.groupByColumn,
        actions: desc.actions,
        components: desc.components,
        filter: desc.filter,
        transforms: desc.transforms,
        titleProperty: desc.titleProperty,
        subtitleProperty: desc.subtitleProperty,
        linkURLProperty: desc.linkURLProperty,
        imageKind: desc.imageKind,
        imageURLProperty: desc.imageURLProperty,
        limit: undefined,
    };
}
